import { useMemo } from "react";
import { ILateBloodDraw } from "@nkr/models/index";
import SortableHeaderComponent from "@nkr/views/admin/center/list/common/sortable-header";
import { Utility } from "@nkr/services/utility";
import {
    CellClassParams,
    ColDef,
    FirstDataRenderedEvent,
    GridReadyEvent,
    IHeaderParams,
    RowDataChangedEvent,
    SortChangedEvent,
    ValueGetterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import BloodDrawDateCell, { IBloodDrawDateCellContext } from "./blood-draw-date-cell";
import { ReportWrapper } from "./styles";

const minCompliantDelta = 5;
const maxCompliantDelta = 7;

interface IProps {
    data: ILateBloodDraw[];
    isEditable: boolean;
    onBloodDrawDateSelected: (serologyTrackingId: number, bloodDrawDate: string) => void;
    onGridReady?: (e: GridReadyEvent) => void;
}

const Grid = (props: IProps) => {
    const onGridReady = (e: GridReadyEvent) => {
        e.api.sizeColumnsToFit();

        if (props.onGridReady) {
            props.onGridReady(e);
        }
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells({ columns: ["rank"], force: true });
    };

    const cols: ColDef[] = [
        {
            colId: "rank",
            headerName: "R#",
            headerClass: "list-header",
            headerComponentFramework: (params: IHeaderParams) => (
                <SortableHeaderComponent {...params}>R#</SortableHeaderComponent>
            ),
            valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1,
            width: 10,
            cellClass: "list-cell",
            sortable: false,
            resizable: false,
        },
        {
            colId: "swap",
            field: "swap",
            headerName: "Swap",
            headerClass: "list-header",
            headerComponentFramework: (params: IHeaderParams) => (
                <SortableHeaderComponent {...params}>Swap</SortableHeaderComponent>
            ),
            width: 85,
            cellClass: "list-cell",
            sortable: true,
            resizable: false,
        },
        {
            colId: "donorCenterAlias",
            field: "donorCenterAlias",
            headerName: "Donor Center",
            headerClass: "list-header",
            headerComponentFramework: (params: IHeaderParams) => (
                <SortableHeaderComponent {...params}>Donor Center</SortableHeaderComponent>
            ),
            width: 155,
            cellClass: "list-cell",
            sortable: true,
            resizable: false,
        },
        {
            colId: "donorAlias",
            field: "donorAlias",
            headerName: "Donor Alias",
            headerClass: "list-header",
            headerComponentFramework: (params: IHeaderParams) => (
                <SortableHeaderComponent {...params}>Donor Alias</SortableHeaderComponent>
            ),
            width: 105,
            cellClass: "list-cell",
            sortable: true,
            resizable: false,
        },
        {
            colId: "recipientCenterAlias",
            field: "recipientCenterAlias",
            headerName: "Recipient Center",
            headerClass: "list-header",
            headerComponentFramework: (params: IHeaderParams) => (
                <SortableHeaderComponent {...params}>Recipient Center</SortableHeaderComponent>
            ),
            width: 155,
            cellClass: "list-cell",
            sortable: true,
            resizable: false,
        },
        {
            colId: "recipientAlias",
            field: "recipientAlias",
            headerName: "Recipient Alias",
            headerClass: "list-header",
            headerComponentFramework: (params: IHeaderParams) => (
                <SortableHeaderComponent {...params}>Recipient Alias</SortableHeaderComponent>
            ),
            width: 105,
            cellClass: "list-cell",
            sortable: true,
            resizable: false,
        },
        {
            colId: "bloodDrawDate",
            field: "bloodDrawDate",
            headerName: "Blood Draw Date",
            headerClass: "list-header",
            headerComponentFramework: (params: IHeaderParams) => (
                <SortableHeaderComponent {...params}>Blood Draw Date</SortableHeaderComponent>
            ),
            cellRendererFramework: BloodDrawDateCell,
            comparator: Utility.dateComparator,
            width: 145,
            cellClass: props.isEditable ? "list-cell list-cell-editable" : "list-cell",
            sortable: true,
            resizable: false,
        },
        {
            colId: "kidneyPickupDate",
            field: "kidneyPickupDate",
            headerName: "Surgery Date",
            headerClass: "list-header",
            headerComponentFramework: (params: IHeaderParams) => (
                <SortableHeaderComponent {...params}>Surgery Date</SortableHeaderComponent>
            ),
            comparator: Utility.dateComparator,
            width: 145,
            cellClass: "list-cell",
            sortable: true,
            resizable: false,
        },
        {
            colId: "delta",
            field: "delta",
            headerName: "Delta (Days)",
            headerClass: "list-header",
            headerComponentFramework: (params: IHeaderParams) => (
                <SortableHeaderComponent {...params}>Delta (Days)</SortableHeaderComponent>
            ),
            cellStyle: (params: CellClassParams) =>
                params.value < minCompliantDelta || params.value > maxCompliantDelta
                    ? { color: "#a94442", fontWeight: "bold" }
                    : null,
            width: 145,
            cellClass: "list-cell",
            sortable: true,
            resizable: false,
        },
    ];

    const columnDefs = useMemo(() => cols, [props.isEditable]);

    const gridContext: IBloodDrawDateCellContext = {
        isEditable: props.isEditable,
        onBloodDrawDateSelected: props.onBloodDrawDateSelected,
    };

    return (
        <ReportWrapper className="ag-theme-bootstrap">
            <AgGridReact
                columnDefs={columnDefs}
                rowData={props.data}
                context={gridContext}
                headerHeight={30}
                groupHeaderHeight={30}
                rowHeight={30}
                suppressRowClickSelection={false}
                suppressCellSelection={true}
                noRowsOverlayComponentFramework={() => <div>No serology blood draws found...</div>}
                onGridReady={onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onRowDataChanged={onRowDataChanged}
                onSortChanged={onSortChanged}
            />
        </ReportWrapper>
    );
};

export default Grid;
